import { useState } from "react";
import Layout from "@/components/feature/Layout";
import PageSEO from "@/components/feature/PageSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { SITE_NAME } from "@/constants/site";

const VALUES = [
  { icon: "ri-stack-line", title: "内容聚合", desc: "汇聚电影、电视剧等多种影视资源，一个平台满足日常观影需求。" },
  { icon: "ri-filter-3-line", title: "便捷筛选", desc: "支持按类型、地区、题材、年份筛选，几秒钟找到想看的作品。" },
  { icon: "ri-hd-line", title: "高清体验", desc: "以高清画质为默认标准，注重播放与浏览过程的使用体验。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "内容按上线时间持续更新，热门作品与最新剧集及时收录。" },
];

const CONTACTS = [
  { icon: "ri-customer-service-2-line", label: "用户反馈", value: "feedback@panda-cinema.com" },
  { icon: "ri-briefcase-line", label: "商务合作", value: "business@panda-cinema.com" },
  { icon: "ri-question-answer-line", label: "服务支持", value: "周一至周五 09:00 - 18:00" },
];

const FAQS = [
  {
    q: "熊猫影院需要注册或登录才能观看吗？",
    a: "当前版本无需注册即可浏览与搜索影视内容，所有页面均可直接访问。后续如开放个性化功能，会另行说明。",
  },
  {
    q: "为什么播放区域显示“暂时无法播放”？",
    a: "详情页的播放区域为演示播放器，用于展示页面结构，真实播放接口尚未接入。接入后即可正常观看。",
  },
  {
    q: "收藏和分享按钮为什么点击后没有变化？",
    a: "收藏与分享目前为前端演示状态，点击会给出功能提示，暂不保存任何数据，也不会上传你的信息。",
  },
  {
    q: "如何快速找到某一类影视作品？",
    a: "可以在顶部搜索框输入关键词，也可以进入电影或电视剧分类页，通过地区、题材、年份与排序快速缩小范围。",
  },
  {
    q: "页面中的影视信息是真实的吗？",
    a: "页面内容均为演示用的虚构数据，影视名称、导演、演员、评分等均非真实信息，仅用于产品展示。",
  },
];

export default function AboutPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <Layout>
      <PageSEO
        title="关于我们 - 熊猫影院平台介绍与联系方式"
        description="了解熊猫影院的平台定位、服务内容、联系方式与常见问题。熊猫影院致力于为影视爱好者提供便捷的影视查询与观看体验。"
        keywords="熊猫影院,关于我们,平台介绍,联系方式,常见问题"
        path="/about"
      />

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <header className="mt-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于我们
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            {SITE_NAME}是一个面向影视爱好者的在线影视内容平台，我们希望用简洁明快的浏览体验，
            让查找和发现好作品这件事变得轻松愉快。
          </p>
        </header>

        <section className="mt-10 rounded-lg border border-background-200/80 bg-background-100/60 p-6 md:p-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            平台定位
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
            熊猫影院定位于「最新电影和电视剧在线播放与影视资源查询平台」，面向喜爱观看电影和电视剧的普通观众与影视爱好者，
            覆盖各年龄层追求高清、丰富多样影视内容的用户群体。平台以专业、品质感的门户风格呈现内容，
            通过清晰的信息层级和高效的操作路径，帮助用户快速搜索、浏览多类型影视资源。
          </p>
          <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
            我们希望用户在这里既能方便地查看作品详情，也能获得良好的观影体验，从而更轻松地享受影视内容带来的乐趣。
          </p>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200/80 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-sm font-medium text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            联系方式
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CONTACTS.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-100/60 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="mt-1 truncate text-sm font-medium text-foreground-900">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            如需与我们联系，请通过上方邮箱留言，我们会尽快回复。
          </p>
        </section>

        <section className="mt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            常见问题
          </h2>
          <div className="mt-5 divide-y divide-background-200/70 overflow-hidden rounded-lg border border-background-200/80 bg-background-100/60">
            {FAQS.map((faq, idx) => (
              <div key={faq.q}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-500">
                    <i
                      className={
                        openIndex === idx ? "ri-subtract-line text-lg" : "ri-add-line text-lg"
                      }
                    ></i>
                  </span>
                </button>
                {openIndex === idx && (
                  <p className="animate-fade-in px-5 pb-5 text-sm leading-relaxed text-foreground-600">
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-lg border border-background-200/80 bg-secondary-50 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            服务声明
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-primary-500"></i>
              本站目前为产品演示版本，页面中的影视名称、导演、演员、评分等均为虚构内容，不对应任何真实作品或个人。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-primary-500"></i>
              播放区域为前端演示播放器，不提供真实播放服务；收藏与分享为演示交互，不保存任何用户数据。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-primary-500"></i>
              平台将持续优化内容组织与浏览体验，后续接入真实数据与播放接口前会另行说明。
            </li>
          </ul>
        </section>
      </div>
    </Layout>
  );
}